/**
 * Professional mobile detection for complex web applications
 * Targets phone-sized screens that can't handle desktop interfaces
 * Allows tablets and larger devices to use the full interface
 */

import { hasFigmaReferrer } from "./parent-messaging";

export const isPhoneSizedDevice = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  // Never show mobile redirect in a host frame. Any framing suppresses it
  // (phone-sized iframes are how the plugins are laid out), which is a mobile
  // UX rule, not a trust decision: which frames the app will actually talk to
  // is `parent-messaging.ts`. The Figma signal below comes from that module so
  // this cannot drift from the messaging policy — it used to be a substring
  // test for `figma.com`, which any host could satisfy by prefixing its own
  // name with it. That is a behaviour change, not only a tightening: a
  // phone-sized visit referred by a lookalike host such as
  // `https://figma.com.evil.example/` now gets the mobile page, where the
  // substring test waved it through to the desktop app.
  // `window.parent !== window` already covers every framed document, so the
  // `ancestorOrigins?.[0]` `figma.com` clause this replaced added nothing and
  // dropping it changes no answer.
  if (
    window.parent !== window ||
    hasFigmaReferrer() ||
    navigator.userAgent.includes("Figma")
  ) {
    return false;
  }

  // Check if user has manually overridden (localStorage preference)
  // why: getItem THROWS (SecurityError) when storage is blocked — sandboxed
  // iframe, blocked third-party cookies — even though window/navigator are
  // present above. This runs from the root route effect on mount and on every
  // resize, so an uncaught throw unmounts the tree. An unreadable preference
  // means "no override", not "crash".
  let userOverride: string | null = null;
  try {
    userOverride = localStorage.getItem("force-desktop-view");
  } catch {
    userOverride = null;
  }
  if (userOverride === "true") {
    return false;
  }

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  // Industry standard breakpoints:
  // - Phones: up to 480px width
  // - Small tablets: 481-768px (allow these to use desktop)
  // - Tablets: 769-1024px (definitely allow these)
  // - Desktop: 1025px+ (definitely allow these)

  const isPhoneWidth = screenWidth <= 480;

  // Additional check: very narrow screens in portrait mode
  // This catches edge cases like narrow browser windows
  const isVeryNarrowPortrait = screenWidth <= 600 && screenHeight > screenWidth;

  // Check user agent for phone-specific patterns (excludes tablets)
  const userAgent = navigator.userAgent.toLowerCase();
  const isPhoneUserAgent =
    /iphone|ipod|android.*mobile|blackberry|opera mini|iemobile|windows phone/.test(
      userAgent,
    );

  // Only block on actual phone-sized screens
  return isPhoneWidth || (isVeryNarrowPortrait && isPhoneUserAgent);
};

/**
 * Paths that may render on phone-sized screens even for logged-in users.
 * Share-link ticket review and auth surfaces must stay reachable on phones.
 */
export function isMobileAllowedPath(pathname: string): boolean {
  if (pathname === "/login" || pathname.startsWith("/login/")) {
    return true;
  }
  return /^\/tickets\/[^/]+\/view\/?$/.test(pathname);
}

/**
 * Allow user to override mobile detection and use desktop version
 */
export const setDesktopViewPreference = (forceDesktop: boolean): void => {
  if (typeof localStorage !== "undefined") {
    // why: writes throw (QuotaExceededError / SecurityError) where reads
    // return null. A blocked write must not turn the user's opt-in into an
    // uncaught error on the page.
    try {
      if (forceDesktop) {
        localStorage.setItem("force-desktop-view", "true");
      } else {
        localStorage.removeItem("force-desktop-view");
      }
    } catch {
      // ignore — the preference is best-effort
    }
    // Reload to apply changes
    window.location.reload();
  }
};
